import { api } from './api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function registerSW() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;
    return reg;
  } catch (e) {
    console.warn('SW register failed', e);
    return null;
  }
}

export async function isPushSubscribed() {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}

/**
 * Activa Web Push: permiso + suscripción del navegador + registro en el servidor.
 * Funciona aunque la pestaña o el navegador estén cerrados (el SW recibe el push).
 */
export async function subscribePush() {
  if (!pushSupported()) throw new Error('Tu navegador no soporta notificaciones push');

  // HTTPS (o localhost) es obligatorio para push.
  if (window.isSecureContext === false) {
    throw new Error('Las notificaciones push requieren HTTPS');
  }

  let perm = Notification.permission;
  if (perm === 'default') {
    perm = await Notification.requestPermission();
  }
  if (perm !== 'granted') throw new Error('Permiso de notificaciones denegado');

  const reg = (await navigator.serviceWorker.getRegistration()) || (await registerSW());
  if (!reg) throw new Error('Este navegador no permitió activar los avisos. Recarga la página e inténtalo de nuevo.');
  await navigator.serviceWorker.ready;

  const { data } = await api.get('/push/vapid-public-key');
  if (!data?.publicKey) throw new Error('Los avisos no están configurados en el servidor. Avisa a quien administra el correo.');

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }
  await api.post('/push/subscribe', { subscription: sub.toJSON() });
  try {
    localStorage.setItem('zuno_push', 'on');
  } catch (_) {}
  return true;
}

/**
 * Si el permiso ya está concedido, re-sincroniza la suscripción con el servidor
 * (p. ej. tras login o cambio de dispositivo). No vuelve a pedir permiso.
 */
export async function ensurePushSubscription() {
  if (!pushSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  try {
    await subscribePush();
    return true;
  } catch (e) {
    console.warn('ensurePushSubscription', e);
    return false;
  }
}

export async function unsubscribePush() {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    const endpoint = sub.endpoint;
    try {
      await sub.unsubscribe();
    } catch (_) {}
    try {
      await api.post('/push/unsubscribe', { endpoint });
    } catch (_) {}
  }
  try {
    localStorage.setItem('zuno_push', 'off');
  } catch (_) {}
}
